import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';
import noop from 'lodash/noop';

import {
    SuiContainer,
    SuiButton,
    SuiConfirmDialogWrap,
    SuiPopoverMenu,
    SuiMenuItem,
    ORIGIN_TOP_RIGHT,
    SIZE_NONE,
} from '@splashthat/sui';

import LogicBranchIcon from 'common/icons/LogicBranch';
import CopyIcon from 'common/icons/Copy';
import TrashIcon from 'common/icons/Trash';
import AddIcon from 'common/icons/AddFill';
import Ticket from 'common/icons/Ticket';
import LockIcon from 'common/icons/LockFill';
import PermissionCheckWrapper from 'utils/PermissionCheckWrapper';

import { TRANSITION_TIMEOUT } from 'common/constants';
import { TICKET_SELECTION_QA } from '../../constants/ticketDataQa';
import FixedControls, { FixedControlLogic, FixedControlLock } from './FixedControls';

const nodeLabelByType = {
    question: 'question',
    content: 'text element',
    default: 'question',
    group: 'question',
};

export const getRemovalConfirmation = (type) =>
    `Are you sure you want to delete this ${nodeLabelByType[type] || nodeLabelByType.default}?`;
const LOGIC_TITLE = 'Edit Logic';
const LOCK_TITLE = 'Locked';
const DUPLICATE_TITLE = 'Duplicate';
const DELETE_TITLE = 'Delete';
const TICKET_SELECTION_TITLE = 'Return to Ticket Editor';
export const ADD_TEMPLATE_TITLE = 'Add Question to Group';
const ADD_TEXT_ELEMENT = 'Add Text Element';
const ADD_QUESTION = 'Add Question';
const BASE_CLASS_NAME = 'FormEditorNodeControls';

export default class FormEditorNodeControls extends Component {
    static propTypes = {
        canUserDelete: PropTypes.bool,
        isActive: PropTypes.bool.isRequired,
        hasLogic: PropTypes.bool.isRequired,
        editingLocked: PropTypes.bool.isRequired,
        nodeLockingAllowed: PropTypes.bool.isRequired,
        onFocusControl: PropTypes.func.isRequired,
        onBlurControl: PropTypes.func.isRequired,
        hideAddQuestionButton: PropTypes.bool.isRequired,
        onClickLogic: PropTypes.func,
        onClickDuplicate: PropTypes.func,
        onClickDelete: PropTypes.func,
        onClickAddTemplate: PropTypes.func,
        onClickAddTextElement: PropTypes.func,
        onClickTicketSelection: PropTypes.func,
        nodeType: PropTypes.string,
    };

    static defaultProps = {
        canUserDelete: true,
        onClickLogic: null,
        onClickDuplicate: null,
        onClickDelete: null,
        onClickAddTemplate: null,
        onClickAddTextElement: noop,
        onClickTicketSelection: null,
        nodeType: '',
    };

    preventPropagation = (ev) => {
        ev.stopPropagation();
    };

    handleDelete = (event) => {
        event && event.stopPropagation();
        this.props.onClickDelete(event);
    };

    render() {
        const {
            canUserDelete,
            isActive,
            hasLogic,
            onFocusControl,
            onBlurControl,
            onClickLogic,
            onClickDuplicate,
            onClickDelete,
            onClickAddTemplate,
            onClickAddTextElement,
            onClickTicketSelection,
            editingLocked,
            nodeLockingAllowed,
            hideAddQuestionButton,
            nodeType,
        } = this.props;

        // we show buttons only if node isn't locked or, if locked, the user has permission to edit.
        const buttonsVisible = !editingLocked || nodeLockingAllowed;

        const editingLogicAllowed = Boolean(onClickLogic) && buttonsVisible;
        const duplicatingAllowed = Boolean(onClickDuplicate) && buttonsVisible;
        const addingTemplateAllowed = Boolean(onClickAddTemplate) && buttonsVisible && !hideAddQuestionButton;
        const deletingAllowed = Boolean(onClickDelete) && buttonsVisible;
        const ticketSelectionAccessAllowed = Boolean(onClickTicketSelection) && buttonsVisible;

        return (
            <SuiContainer>
                <SuiConfirmDialogWrap dialogBody={getRemovalConfirmation(nodeType)} onConfirm={this.handleDelete}>
                    {(onClickConfirmation) => (
                        <CSSTransition
                            in={isActive}
                            unmountOnExit
                            mountOnEnter
                            timeout={TRANSITION_TIMEOUT}
                            classNames={`${BASE_CLASS_NAME}__button-transition`}
                        >
                            <SuiContainer
                                variation="flex"
                                className={[
                                    `${BASE_CLASS_NAME}__button-container`,
                                    isActive ? `${BASE_CLASS_NAME}__button-container--active` : null,
                                ]
                                    .filter((v) => v)
                                    .join(' ')}
                            >
                                {editingLogicAllowed && (
                                    <SuiButton
                                        variation="navigation"
                                        size="small"
                                        icon={LogicBranchIcon}
                                        title={LOGIC_TITLE}
                                        aria-label={LOGIC_TITLE}
                                        onClick={onClickLogic}
                                        onFocus={onFocusControl}
                                        onBlur={onBlurControl}
                                    />
                                )}
                                {!editingLocked && duplicatingAllowed && (
                                    <SuiButton
                                        variation="navigation"
                                        size="small"
                                        icon={CopyIcon}
                                        title={DUPLICATE_TITLE}
                                        aria-label={DUPLICATE_TITLE}
                                        onClick={onClickDuplicate}
                                        onFocus={onFocusControl}
                                        onBlur={onBlurControl}
                                    />
                                )}
                                {addingTemplateAllowed && (
                                    <SuiPopoverMenu
                                        popoverProps={{
                                            anchorOrigin: ORIGIN_TOP_RIGHT,
                                            targetOrigin: ORIGIN_TOP_RIGHT,
                                            targetWidth: SIZE_NONE,
                                            targetHeight: SIZE_NONE,
                                        }}
                                        buttonElement={
                                            <SuiButton
                                                variation="navigation"
                                                size="small"
                                                icon={AddIcon}
                                                title={ADD_TEMPLATE_TITLE}
                                                aria-label={ADD_TEMPLATE_TITLE}
                                                onClick={this.preventPropagation}
                                                onFocus={onFocusControl}
                                                onBlur={onBlurControl}
                                            />
                                        }
                                    >
                                        <SuiMenuItem label={ADD_QUESTION} onClick={onClickAddTemplate} />
                                        <SuiMenuItem label={ADD_TEXT_ELEMENT} onClick={onClickAddTextElement} />
                                    </SuiPopoverMenu>
                                )}
                                {deletingAllowed && (
                                    <PermissionCheckWrapper permission={canUserDelete}>
                                        {(restProps) => (
                                            <SuiButton
                                                variation="navigation"
                                                size="small"
                                                icon={TrashIcon}
                                                title={DELETE_TITLE}
                                                aria-label={DELETE_TITLE}
                                                onClick={onClickConfirmation}
                                                onFocus={onFocusControl}
                                                onBlur={onBlurControl}
                                                disabled={!canUserDelete}
                                                {...restProps}
                                            />
                                        )}
                                    </PermissionCheckWrapper>
                                )}
                                {/* on hover, show the lock only if node is locked and user can't modify */}
                                {editingLocked && !nodeLockingAllowed && (
                                    <SuiButton
                                        variation="navigation"
                                        size="small"
                                        icon={LockIcon}
                                        title={LOCK_TITLE}
                                        aria-label={LOCK_TITLE}
                                    />
                                )}
                            </SuiContainer>
                        </CSSTransition>
                    )}
                </SuiConfirmDialogWrap>

                {ticketSelectionAccessAllowed && (
                    <CSSTransition
                        in={isActive}
                        unmountOnExit
                        mountOnEnter
                        timeout={TRANSITION_TIMEOUT}
                        classNames={`${BASE_CLASS_NAME}__flag-transition`}
                    >
                        <SuiContainer className={`${BASE_CLASS_NAME}__flag-container`}>
                            <SuiButton
                                data-qa={`${TICKET_SELECTION_QA}__editor__button`}
                                variation="navigation"
                                size="small"
                                icon={Ticket}
                                title={TICKET_SELECTION_TITLE}
                                aria-label={TICKET_SELECTION_TITLE}
                                onClick={onClickTicketSelection}
                                onFocus={onFocusControl}
                                onBlur={onBlurControl}
                            />
                        </SuiContainer>
                    </CSSTransition>
                )}
                <FixedControls hidden={isActive}>
                    {hasLogic && (
                        <FixedControlLogic onClick={onClickLogic} onFocus={onFocusControl} onBlur={onBlurControl} />
                    )}
                    {editingLocked && <FixedControlLock />}
                </FixedControls>
            </SuiContainer>
        );
    }
}
